/**
 * Quick Add Panel Manager - Moon Moon Agency
 * Centered modal: color swatches → navigate to product, size buttons → stay in panel.
 * ATC handled by Prestige <product-form> → cart:change → drawer opens.
 */

const QuickAddPanelManager = {
  config: {
    selectors: {
      open: '[data-quick-add-panel-open]',
      close: '[data-quick-add-panel-close]',
      galleryNext: '[data-quick-add-gallery-next]',
      galleryTrack: '.mm-quick-add-panel__gallery-track',
      galleryItem: '.mm-quick-add-panel__gallery-item',
      sizesContainer: '[data-qap-sizes]',
      sizeBtn: '.mm-qap-size-btn:not(.mm-qap-size-btn--unavailable)',
      variantIdInput: '[data-selected-variant-id]',
      atcBtn: 'button[name="add"]',
      variantsJson: '[data-qap-variants-json]',
      colorLabel: '.mm-selected-color-label',
      colorThumb: '.variant-picker__option-values .thumbnail-swatch[data-label]',
    },
    classes: {
      open: 'mm-quick-add-panel--open',
      overlayVisible: 'mm-quick-add-panel__overlay--visible',
      sizeActive: 'mm-qap-size-btn--active',
    },
  },

  _activePanel: null,
  _activeOverlay: null,
  _boundKeyDown: null,
  _splidesMounted: new WeakSet(),

  init() {
    this._boundKeyDown = this._onKeyDown.bind(this);
    document.addEventListener('click', this._onDocumentClick.bind(this));
    window.log?.('info', '🛒 [QUICK_ADD_PANEL]', 'Initialized');
  },

  /* ── Open / Close ── */

  open(panelId) {
    if (this._activePanel) this.close();

    const panel = document.getElementById('mm-quick-add-panel-' + panelId);
    if (!panel) return;

    const overlay = document.querySelector(`[data-quick-add-panel-overlay="${CSS.escape(panelId)}"]`);
    this._activePanel = panel;
    this._activeOverlay = overlay;

    panel.removeAttribute('hidden');
    this._mountSplides(panel);
    this._bindColorHover(panel);
    requestAnimationFrame(() => {
      panel.classList.add(this.config.classes.open);
      overlay?.classList.add(this.config.classes.overlayVisible);
    });

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._boundKeyDown);
    panel.querySelector(this.config.selectors.close)?.focus();
  },

  close() {
    const panel = this._activePanel;
    const overlay = this._activeOverlay;
    if (!panel) return;

    panel.classList.remove(this.config.classes.open);
    overlay?.classList.remove(this.config.classes.overlayVisible);

    const onEnd = () => {
      panel.setAttribute('hidden', '');
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);

    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._boundKeyDown);
    this._activePanel = null;
    this._activeOverlay = null;
  },

  /* ── Splide init ── */

  _mountSplides(panel) {
    panel.querySelectorAll('.mm-splide').forEach(el => {
      if (this._splidesMounted.has(el)) return;
      const options = el.dataset.splide ? JSON.parse(el.dataset.splide) : {};
      new Splide(el, options).mount();
      this._splidesMounted.add(el);
    });
  },

  /* ── Variant selection via size buttons ── */

  _selectSize(btn) {
    const sizes = btn.closest(this.config.selectors.sizesContainer);
    if (!sizes) return;

    const optionIndex = parseInt(btn.dataset.optionIndex, 10);
    const value = btn.dataset.value;

    /* Toggle active state */
    const optionContainer = sizes.querySelector(`[data-qap-option="${optionIndex}"]`);
    optionContainer?.querySelectorAll('.mm-qap-size-btn').forEach(b => {
      b.classList.toggle(this.config.classes.sizeActive, b === btn);
    });

    /* Find matching variant */
    const form = sizes.closest('form');
    if (!form) return;

    const variants = this._getVariants(sizes);
    if (!variants.length) return;

    /* Collect all currently selected options */
    const selectedOptions = {};
    sizes.querySelectorAll(`[data-qap-option]`).forEach(container => {
      const idx = container.dataset.qapOption;
      const activeBtn = container.querySelector('.' + this.config.classes.sizeActive);
      if (activeBtn) selectedOptions[`option${parseInt(idx, 10) + 1}`] = activeBtn.dataset.value;
    });

    const variant = variants.find(v =>
      Object.keys(selectedOptions).every(key => v[key] === selectedOptions[key])
    );

    if (!variant) return;

    /* Update hidden input → drives <product-form> ATC */
    const idInput = form.querySelector(this.config.selectors.variantIdInput);
    if (idInput) idInput.value = variant.id;

    /* Update ATC button (outside form, linked via form attribute) */
    const panel = form.closest('.mm-quick-add-panel');
    const atcBtn = panel?.querySelector(this.config.selectors.atcBtn);
    if (atcBtn) atcBtn.disabled = !variant.available;
  },

  _getVariants(sizesEl) {
    if (sizesEl._variantData) return sizesEl._variantData;
    try {
      const json = sizesEl.querySelector(this.config.selectors.variantsJson);
      sizesEl._variantData = json ? JSON.parse(json.textContent.trim()) : [];
    } catch { sizesEl._variantData = []; }
    return sizesEl._variantData;
  },

  /* ── Color thumbnail hover ── */

  _bindColorHover(panel) {
    console.log('[QAP hover] panel id:', panel.id);
    console.log('[QAP hover] .thumbnail-swatch dans panel:', panel.querySelectorAll('.thumbnail-swatch').length);
    console.log('[QAP hover] .thumbnail-swatch[data-label] dans panel:', panel.querySelectorAll('.thumbnail-swatch[data-label]').length);
    console.log('[QAP hover] .variant-picker__option-values dans panel:', panel.querySelectorAll('.variant-picker__option-values').length);
    console.log('[QAP hover] premier thumbnail HTML:', panel.querySelectorAll('.thumbnail-swatch')[0]?.outerHTML?.slice(0, 200));

    const thumbs = panel.querySelectorAll(this.config.selectors.colorThumb);
    console.log('[QAP hover] thumbs trouvés:', thumbs.length, '| sélecteur:', this.config.selectors.colorThumb);

    if (!thumbs.length) {
      console.warn('[QAP hover] Aucun thumbnail trouvé — vérifier le HTML du panel');
      return;
    }

    const labelEl = panel.querySelector(this.config.selectors.colorLabel);
    console.log('[QAP hover] labelEl:', labelEl, '| texte actuel:', labelEl?.textContent.trim());

    const defaultLabel = labelEl?.textContent.trim() ?? '';
    const track = panel.querySelector(this.config.selectors.galleryTrack);
    console.log('[QAP hover] track:', track);

    const defaultImages = track
      ? Array.from(track.querySelectorAll('.mm-quick-add-panel__gallery-item')).map(el => el.innerHTML)
      : [];
    console.log('[QAP hover] defaultImages capturées:', defaultImages.length);

    thumbs.forEach((thumb, i) => {
      const raw = thumb.dataset.images;
      console.log(`[QAP hover] thumb[${i}] data-label="${thumb.dataset.label}" data-images="${raw?.slice(0, 80)}..."`);

      const images = raw
        ? raw.split('~').filter(Boolean).map(entry => {
            const sep = entry.indexOf('|');
            return { src: entry.slice(0, sep), alt: entry.slice(sep + 1) };
          })
        : null;
      console.log(`[QAP hover] thumb[${i}] images parsées:`, images?.length ?? 'null');

      const onEnter = () => {
        console.log(`[QAP hover] mouseenter thumb[${i}] label="${thumb.dataset.label}"`);
        if (labelEl) labelEl.textContent = thumb.dataset.label ?? '';
        if (images) this._swapGallery(track, images);
      };
      const onLeave = () => {
        console.log(`[QAP hover] mouseleave thumb[${i}]`);
        if (labelEl) labelEl.textContent = defaultLabel;
        this._restoreGallery(track, defaultImages);
      };

      thumb.removeEventListener('mouseenter', thumb._qaOnEnter);
      thumb.removeEventListener('mouseleave', thumb._qaOnLeave);
      thumb._qaOnEnter = onEnter;
      thumb._qaOnLeave = onLeave;
      thumb.addEventListener('mouseenter', onEnter);
      thumb.addEventListener('mouseleave', onLeave);
    });
  },

  _swapGallery(track, images) {
    if (!track || !images?.length) return;

    track.innerHTML = images.map(img =>
      `<div class="mm-quick-add-panel__gallery-item mm-relative mm-overflow-hidden">
        <img src="${img.src}" alt="${img.alt}" class="mm-quick-add-panel__gallery-img" loading="lazy">
      </div>`
    ).join('');
    track.scrollTo({ left: 0, behavior: 'instant' });
  },

  _restoreGallery(track, defaultImages) {
    if (!track || !defaultImages.length) return;
    track.innerHTML = defaultImages.map(html =>
      `<div class="mm-quick-add-panel__gallery-item mm-relative mm-overflow-hidden">${html}</div>`
    ).join('');
    track.scrollTo({ left: 0, behavior: 'instant' });
  },

  /* ── Gallery scroll ── */

  _scrollGalleryNext(btn) {
    const track = btn.closest('.mm-quick-add-panel__gallery')
      ?.querySelector(this.config.selectors.galleryTrack);
    if (!track) return;
    const itemWidth = track.querySelector(this.config.selectors.galleryItem)?.offsetWidth || 275;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const next = track.scrollLeft + itemWidth;
    track.scrollTo({ left: next >= maxScroll ? 0 : next, behavior: 'smooth' });
  },

  /* ── Events ── */

  _onKeyDown(e) {
    if (e.key === 'Escape') this.close();
  },

  _onDocumentClick(e) {
    /* Color thumbnail click inside panel → swap gallery, no navigation */
    const thumb = e.target.closest('.thumbnail-swatch[data-label]');
    if (thumb && thumb.closest('.mm-quick-add-panel')) {
      e.preventDefault();
      const panel = thumb.closest('.mm-quick-add-panel');
      const labelEl = panel.querySelector(this.config.selectors.colorLabel);
      if (labelEl) labelEl.textContent = thumb.dataset.label ?? '';
      const raw = thumb.dataset.images;
      if (raw) {
        const images = raw.split('~').filter(Boolean).map(entry => {
          const sep = entry.indexOf('|');
          return { src: entry.slice(0, sep), alt: entry.slice(sep + 1) };
        });
        const track = panel.querySelector(this.config.selectors.galleryTrack);
        this._swapGallery(track, images);
      }
      panel.querySelectorAll('.thumbnail-swatch[data-label]').forEach(t => {
        t.classList.toggle('is-selected', t === thumb);
      });
      return;
    }

    /* Size button click */
    const sizeBtn = e.target.closest(this.config.selectors.sizeBtn);
    if (sizeBtn && sizeBtn.closest('.mm-quick-add-panel')) {
      this._selectSize(sizeBtn);
      return;
    }

    /* Open */
    const openBtn = e.target.closest(this.config.selectors.open);
    if (openBtn) {
      this.open(openBtn.dataset.quickAddPanelOpen);
      return;
    }

    /* Gallery arrow */
    const galleryBtn = e.target.closest(this.config.selectors.galleryNext);
    if (galleryBtn) {
      this._scrollGalleryNext(galleryBtn);
      return;
    }

    /* Close button (×) */
    if (e.target.closest(this.config.selectors.close)) {
      this.close();
      return;
    }

    /* ATC button → close panel after click */
    const atcBtn = e.target.closest(this.config.selectors.atcBtn);
    if (atcBtn && atcBtn.closest('.mm-quick-add-panel')) {
      this.close();
      return;
    }

    /* Clic hors dialog → close */
    if (this._activePanel && e.target === this._activePanel) {
      this.close();
    }
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => QuickAddPanelManager.init());
} else {
  QuickAddPanelManager.init();
}
